import { tv } from '@utilities';

import type { ColumnMeta } from '../types';

export const tableRootVariants = tv({
  slots: {
    container:
      'group/table w-full overflow-auto rounded-sm border border-active bg-content',
    table: 'w-max min-w-full h-fit border-separate border-spacing-0',
  },
});

export const tableHeaderVariants = tv({
  slots: {
    head: '',
    row: 'border-b border-active group-data-[scrolled-top=true]/table:!shadow-[0_1px_0_rgba(15,23,42,0.06),0_10px_18px_-14px_rgba(15,23,42,0.45)]',
    cell: 'p-0 m-0 overflow-hidden border-0 bg-light text-small font-normal text-textLight',
    button:
      'flex w-full items-center gap-x-xs border-0 bg-transparent px-xl py-md font-normal text-inherit shadow-none hover:bg-transparent',
    label: 'truncate',
    icon: 'shrink-0 text-text',
  },
  variants: {
    stickyHeader: {
      true: { head: 'sticky top-0 z-10' },
      false: { head: '' },
    },
    align: {
      left: { cell: 'text-left justify-start', button: 'justify-start' },
      center: { cell: 'text-center justify-center', button: 'justify-center' },
      right: { cell: 'text-right justify-end', button: 'justify-end' },
    },
    sortable: {
      true: { button: 'cursor-pointer hover:text-text' },
      false: { button: 'cursor-default' },
    },
    readonly: {
      true: { button: 'pointer-events-none' },
      false: { button: '' },
    },
    stickySide: {
      left: {
        cell: [
          'sticky z-[13] bg-light [backface-visibility:hidden] overflow-visible',
          "after:content-[''] after:absolute after:inset-y-0 after:right-0 after:w-5",
          'after:translate-x-full after:pointer-events-none',
          'after:bg-gradient-to-r after:from-black/[.06] after:to-transparent',
          'after:opacity-0 after:transition-opacity after:duration-200',
          'group-data-[scrolled-left=true]/table:after:opacity-100',
        ].join(' '),
      },
      right: {
        cell: [
          'sticky z-[13] bg-light [backface-visibility:hidden] overflow-visible',
          "after:content-[''] after:absolute after:inset-y-0 after:left-0 after:w-5",
          'after:-translate-x-full after:pointer-events-none',
          'after:bg-gradient-to-l after:from-black/[.06] after:to-transparent',
          'after:opacity-0 after:transition-opacity after:duration-200',
          'group-data-[scrolled-right=true]/table:after:opacity-100',
        ].join(' '),
      },
      none: { cell: '' },
    },
  },
  defaultVariants: {
    stickyHeader: true,
    align: 'left',
    sortable: false,
    readonly: false,
    stickySide: 'none',
  },
});

export const tableRowVariants = tv({
  base: 'group/tablerow bg-content transition-colors hover:bg-light/60',
  variants: {
    clickable: {
      true: 'cursor-pointer',
      false: '',
    },
  },
  defaultVariants: {
    clickable: false,
  },
});

export const tableCellVariants = tv({
  slots: {
    cell: 'border-b border-active p-0 text-small text-text transition-def',
    content: 'px-xl py-lg',
  },
  variants: {
    align: {
      left: { cell: 'text-left justify-start', content: 'text-left' },
      center: { cell: 'text-center justify-center', content: 'text-center' },
      right: { cell: 'text-right justify-end', content: 'text-right' },
    },
    verticalAlign: {
      top: { cell: 'align-top' },
      middle: { cell: 'align-middle' },
    },
    stickySide: {
      left: {
        cell: [
          'sticky z-[2] bg-content [backface-visibility:hidden]',
          "after:content-[''] after:absolute after:inset-y-0 after:right-0 after:w-5",
          'after:translate-x-full after:pointer-events-none',
          'after:bg-gradient-to-r after:from-black/[.06] after:to-transparent',
          'after:opacity-0 after:transition-opacity after:duration-200',
          'group-data-[scrolled-left=true]/table:after:opacity-100',
        ].join(' '),
      },
      right: {
        cell: [
          'sticky z-[2] bg-content [backface-visibility:hidden]',
          "after:content-[''] after:absolute after:inset-y-0 after:left-0 after:w-5",
          'after:-translate-x-full after:pointer-events-none',
          'after:bg-gradient-to-l after:from-black/[.06] after:to-transparent',
          'after:opacity-0 after:transition-opacity after:duration-200',
          'group-data-[scrolled-right=true]/table:after:opacity-100',
        ].join(' '),
      },
      none: { cell: '' },
    },
    truncate: {
      true: { content: 'truncate' },
      false: { content: '' },
    },
    clamp: {
      true: { content: 'line-clamp-4 break-words' },
      false: { content: '' },
    },
    isLastRow: {
      true: { cell: 'border-b-0' },
      false: { cell: '' },
    },
  },
  defaultVariants: {
    align: 'left',
    verticalAlign: 'middle',
    stickySide: 'none',
    truncate: false,
    clamp: false,
    isLastRow: false,
  },
});

export const tableLoaderVariants = tv({
  slots: {
    body: '',
    row: '',
    cell: 'border-b border-active px-0 overflow-hidden',
    wrapper: 'bg-content px-xl py-lg',
    pulse: 'h-[4.8rem] rounded-sm bg-light animate-pulse',
  },
  variants: {
    isLast: {
      true: { cell: 'border-b-0' },
      false: { cell: '' },
    },
  },
  defaultVariants: {
    isLast: false,
  },
});

export const tableEmptyStateVariants = tv({
  slots: {
    cell: 'border-b-0 p-0',
    content:
      'flex min-h-[20rem] flex-col items-center justify-center gap-y-md px-xl py-4xl text-center',
    icon: 'flex size-[4.8rem] items-center justify-center rounded-sm border border-active bg-light text-textLight',
    title: 'text-small font-medium text-textLight',
  },
});

export const getColumnSizingStyle = <T>(column: ColumnMeta<T>) => ({
  width: column.width,
  minWidth: column.minWidth,
});

export const getStickyStyle = <T>(
  column: ColumnMeta<T>,
  extraStyle?: NullableCSS | null
) => ({
  ...getColumnSizingStyle(column),
  ...(extraStyle || {}),
  ...(column.stickySide
    ? { [column.stickySide]: column.stickyOffset ?? 0 }
    : {}),
});
